import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Grid, Stack, Select, MenuItem, FormControl,
  InputLabel, Skeleton, Alert, Table, TableHead, TableRow, TableCell, TableBody, Fade,
  Button, CircularProgress, TextField, Tabs, Tab, Pagination,
  Dialog, DialogTitle, DialogContent, Link, IconButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DataUsageOutlinedIcon from '@mui/icons-material/DataUsageOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Navbar from '../components/Navbar.js';
import StripePaymentForm from '../components/StripePaymentForm.js';
import CancelSubscriptionCard from '../components/CancelSubscriptionCard.js';
import BillingManageCard from '../components/BillingManageCard.js';
import NotificationsCard from '../components/NotificationsCard.js';
import { UsagePanel, UsageHistory } from '../components/UsageTab.js';
import InsightsBreakdown from '../components/InsightsBreakdown.js';
import StackedBars from '../components/StackedBars.js';
import { typeGroups, GROUP_ORDER, groupOf } from '../components/messageTypes.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';

const SUB_STATUS = {
  trialing: { label: 'Free trial', color: '#00838f', bg: '#e0f7f9' },
  incomplete: { label: 'Awaiting payment', color: '#8a6d00', bg: '#fff8e1' },
  active: { label: 'Active', color: '#2e7d32', bg: '#e8f5e9' },
  past_due: { label: 'Past due', color: '#c62828', bg: '#fce4ec' },
  canceled: { label: 'Canceled', color: '#c62828', bg: '#fce4ec' },
};
const WINDOWS = [{ label: 'Last 7 days', days: 7 }, { label: 'Last 30 days', days: 30 }, { label: 'Last 90 days', days: 90 }, { label: 'All time', days: 0 }];
const PAY_PAGE_SIZE = 10;
const fmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return s; } };
const money = (a, c) => `${Number(a).toFixed(2)} ${c || ''}`.trim();
const cardSx = { p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' };

// Human label for a monthly volume cap: 0/undefined => Unlimited.
const capText = (n) => (n == null || n === 0 ? 'Unlimited' : Number(n).toLocaleString());
// Prettify a features flag key, e.g. "priority_support" -> "Priority support".
const featureLabel = (k) => k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());


// Lets an org-admin pick a plan when none is set yet. Fetches the available
// packages and posts the choice; calls onChosen with the selected package.
// Stripe invoices (with downloadable PDFs) for the Payments tab. Self-fetching.
function StripeInvoices({ orgId, refreshKey = 0 }) {
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    apiCall(ENDPOINTS.ORG_INVOICES(orgId))
      .then((d) => { if (alive) { setInvoices((d && d.invoices) || []); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [orgId, refreshKey]);
  if (!loaded || invoices.length === 0) return null;
  return (
    <Box sx={{ mt: 3, overflowX: 'auto' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Invoices</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Date</TableCell><TableCell align="right">Amount</TableCell>
          <TableCell>Status</TableCell><TableCell align="right">Receipt</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {invoices.map((i) => (
            <TableRow key={i.id}>
              <TableCell>{fmt(i.created)}</TableCell>
              <TableCell align="right">{money(i.amount, i.currency)}</TableCell>
              <TableCell><Chip size="small" label={i.status} variant="outlined" /></TableCell>
              <TableCell align="right">{i.invoice_pdf
                ? <Link href={i.invoice_pdf} target="_blank" rel="noopener">PDF</Link> : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ChoosePackage({ orgId, isTrial, onChosen, changeMode = false, currentSlug = '',
  whenNote = 'Your new plan takes effect at your next billing cycle — you keep your current plan until then.' }) {
  const [pkgs, setPkgs] = useState([]);
  const [slug, setSlug] = useState(currentSlug || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    apiCall(ENDPOINTS.PACKAGES).then((d) => {
      if (!alive) return;
      const list = (d && d.packages) || [];
      setTimeout(() => { setPkgs(list); setSlug((p) => p || currentSlug || (list[0] ? list[0].slug : '')); }, 0);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async () => {
    if (!slug || (changeMode && slug === currentSlug)) return;
    setBusy(true); setErr('');
    try {
      const res = await apiCall(ENDPOINTS.ORG_CHOOSE_PACKAGE(orgId), {
        method: 'POST', body: JSON.stringify({ package: slug }),
      });
      if (onChosen) onChosen(res);
    } catch (e) {
      setErr((e && (e.message || (e.body && e.body.detail))) || 'Could not update the plan. Please try again.');
    }
    setBusy(false);
  };

  const wrapSx = changeMode
    ? { mt: 1.5 }
    : { mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' };

  return (
    <Box sx={wrapSx}>
      {!changeMode && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Choose your plan</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            No plan is selected yet{isTrial ? ' — you have full access during your free trial. Pick the plan you want when it ends.' : '.'}
          </Typography>
        </>
      )}
      {changeMode && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {whenNote}
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="choose-plan-label">Plan</InputLabel>
          <Select labelId="choose-plan-label" label="Plan" value={slug} onChange={(e) => setSlug(e.target.value)}>
            {pkgs.map((p) => (
              <MenuItem key={p.slug} value={p.slug}>
                {p.name} — ${p.monthly_price}/mo{p.sms_allowed === false ? ' · email only' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={choose} disabled={busy || !slug || (changeMode && slug === currentSlug)}>
          {busy ? <CircularProgress size={18} /> : (changeMode ? 'Schedule change' : 'Choose plan')}
        </Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{err}</Alert>}
    </Box>
  );
}

// What the chosen package grants (channels, monthly caps, provisioning limits, features).
// On a free trial we note that all features are unlocked during the trial. When no
// package is set, org-admins get a picker to choose one.
function PackageGrants({ pkg, isTrial, orgId, pending, pendingAt, startedAt, ending, onChosen }) {
  const [editing, setEditing] = useState(false);
  const [cancelingChange, setCancelingChange] = useState(false);
  const cancelScheduledChange = async () => {
    setCancelingChange(true);
    try {
      await apiCall(ENDPOINTS.ORG_CANCEL_SCHEDULED_CHANGE(orgId), { method: 'POST' });
      onChosen && onChosen();
    } catch { /* leave the schedule as-is on failure */ }
    setCancelingChange(false);
  };
  // No active package yet (typically during a free trial = full access). If a paid
  // tier is already queued to start after the trial, show it; otherwise offer the picker.
  if (!pkg) {
    return (
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {pending ? (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Your plan</Typography>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Your <strong>{pending.name}</strong> plan starts {pendingAt ? `on ${fmt(pendingAt)}` : 'when your free trial ends'}.
              {isTrial ? ' You have full access until then.' : ''}
            </Alert>
            <Button size="small" onClick={() => setEditing((v) => !v)} sx={{ mt: 1, textTransform: 'none' }}>
              {editing ? 'Cancel' : 'Change plan'}
            </Button>
            {editing && (
              <ChoosePackage
                orgId={orgId} isTrial={isTrial} changeMode currentSlug={pending.slug}
                whenNote="Your new plan takes effect when your free trial ends."
                onChosen={() => { setEditing(false); onChosen && onChosen(); }}
              />
            )}
          </>
        ) : (
          <ChoosePackage orgId={orgId} isTrial={isTrial} onChosen={onChosen} />
        )}
      </Box>
    );
  }
  const enabledFeatures = Object.entries(pkg.features || {}).filter(([, v]) => v).map(([k]) => k);
  const chip = (label, color = 'default') => (
    <Chip key={label} size="small" variant="outlined" label={label} color={color} sx={{ fontWeight: 600 }} />
  );
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" fontWeight={700}>{pkg.name} plan</Typography>
        <Typography variant="caption" color="text.secondary">
          ${pkg.monthly_price}/mo{pkg.currency ? ` ${pkg.currency}` : ''}
        </Typography>
        {isTrial && <Chip size="small" label="Free trial — full access" sx={{ bgcolor: '#e0f7f9', color: '#00838f', fontWeight: 600 }} />}
        <Box sx={{ flex: 1 }} />
        {/* No plan changes while the subscription is ending — resume it first. */}
        {!ending && (
          <Button size="small" onClick={() => setEditing((v) => !v)} sx={{ textTransform: 'none' }}>
            {editing ? 'Cancel' : (pending ? 'Edit scheduled change' : 'Change plan')}
          </Button>
        )}
      </Stack>
      {pkg.tagline && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: startedAt ? 0.25 : 1.5 }}>{pkg.tagline}</Typography>}
      {startedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Plan started {fmt(startedAt)}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {chip(pkg.sms_allowed ? `SMS: ${capText(pkg.sms_limit)}/mo` : 'SMS: not included', pkg.sms_allowed ? 'default' : 'warning')}
        {chip(pkg.email_allowed ? `Email: ${capText(pkg.email_limit)}/mo` : 'Email: not included')}
        {chip(`Categories: ${pkg.max_categories == null ? 'Unlimited' : pkg.max_categories}`)}
        {chip(`Admin users: ${pkg.max_config_users == null ? 'Unlimited' : pkg.max_config_users}`)}
        {enabledFeatures.map((f) => chip(featureLabel(f)))}
      </Stack>

      {/* A queued change takes effect at the next billing cycle — with an option to undo it.
          Hidden while the sub is ending (the change won't apply); the ending notice shows instead. */}
      {pending && !ending && (
        <Alert
          severity="info" sx={{ mt: 1.5, borderRadius: 2 }}
          action={(
            <Button color="inherit" size="small" disabled={cancelingChange}
              onClick={cancelScheduledChange} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {cancelingChange ? <CircularProgress size={16} /> : 'Cancel change'}
            </Button>
          )}
        >
          Your plan changes to <strong>{pending.name}</strong>
          {pendingAt ? ` on ${fmt(pendingAt)}` : ' at your next billing cycle'}. Until then you keep your current plan.
        </Alert>
      )}

      {/* Change flow — the switch is scheduled (trial end or next billing cycle), not applied now. */}
      {editing && (
        <ChoosePackage
          orgId={orgId}
          isTrial={isTrial}
          currentSlug={pending ? pending.slug : pkg.slug}
          changeMode
          whenNote={isTrial
            ? 'Your new plan takes effect when your free trial ends.'
            : 'Your new plan takes effect at your next billing cycle — you keep your current plan until then.'}
          onChosen={() => { setEditing(false); onChosen && onChosen(); }}
        />
      )}
    </Box>
  );
}

// Invoice-style per-day CSV: appointments split (walk-in/scheduled) and every notification group
// broken out by SMS + Email, with per-day totals and a grand TOTAL row at the bottom. Reflects
// whatever scope (org / category) the dashboard is on.
export function downloadUsageCsv(insights, name) {
  const dates = (insights?.dates || []).map((d) => String(d).slice(0, 10));
  const kind = insights?.appointments_by_kind || {};
  const smsByType = insights?.sms_by_type || {};
  const emailByType = insights?.email_by_type || {};
  const at = (byType, i, group) => Object.entries(byType)
    .reduce((s, [t, arr]) => s + (groupOf(t) === group ? (arr[i] || 0) : 0), 0);

  const header = ['date', 'walk_in', 'scheduled', 'appointments'];
  GROUP_ORDER.forEach((g) => header.push(`${g}_sms`, `${g}_email`));
  header.push('sms_total', 'email_total', 'messages_total');

  const totals = new Array(header.length).fill(0);
  totals[0] = 'TOTAL';
  const rows = dates.map((d, i) => {
    const walk = kind.walk_in?.[i] || 0;
    const sched = kind.scheduled?.[i] || 0;
    const row = [d, walk, sched, walk + sched];
    let smsT = 0;
    let emailT = 0;
    GROUP_ORDER.forEach((g) => {
      const s = at(smsByType, i, g);
      const e = at(emailByType, i, g);
      row.push(s, e); smsT += s; emailT += e;
    });
    row.push(smsT, emailT, smsT + emailT);
    for (let k = 1; k < row.length; k += 1) totals[k] += row[k];
    return row;
  });
  const csv = [header.join(','), ...rows.map((r) => r.join(',')), totals.join(',')].join('\n');
  const from = dates[0];
  const to = dates[dates.length - 1];
  const stamp = from && to ? (from === to ? from : `${from}_to_${to}`) : new Date().toISOString().slice(0, 10);
  const safeName = ((name || 'usage').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '')) || 'usage';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}_usage_${stamp}.csv`;   // e.g. Twilight_Cafe_usage_2026-07-01_to_2026-07-08.csv
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}


function StatCard({ icon, label, value }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>{icon}</Box>
      <Box>
        <Typography variant="h5" fontWeight={800} lineHeight={1.1}>{value ?? 0}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    </Paper>
  );
}

export default function OrgBillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orgAccess = (() => { try { return JSON.parse(localStorage.getItem('orgAccess') || '[]'); } catch { return []; } })();
  const queryOrg = searchParams.get('org');
  const [orgId, setOrgId] = useState(queryOrg ? Number(queryOrg) : (orgAccess[0] || null));
  const [orgName, setOrgName] = useState('');
  const [tab, setTab] = useState(0);
  // Bumped by the refresh button; self-fetching child components watch it to re-run their fetch.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState(30);
  const [bucket, setBucket] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Insights category filter ('' = all categories), with the org's categories for the dropdown.
  const [insightCat, setInsightCat] = useState('');
  const [insightCats, setInsightCats] = useState([]);

  const [sub, setSub] = useState(undefined);
  // Package-gated capabilities from the subscription endpoint (analytics -> Insights tab,
  // advanced_analytics -> CSV export). Default off until we know.
  const [caps, setCaps] = useState({ analytics: false, advanced_analytics: false });
  // If the Insights tab is selected but analytics isn't available on this plan, fall back.
  useEffect(() => {
    if (tab === 1 && !caps.analytics) setTab(0);
  }, [tab, caps.analytics]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [payments, setPayments] = useState([]);
  const [payCount, setPayCount] = useState(0);
  const [payPage, setPayPage] = useState(1);
  const [payLoading, setPayLoading] = useState(false);

  const [subscribing, setSubscribing] = useState(false);
  const [subMsg, setSubMsg] = useState('');
  const [resuming, setResuming] = useState(false);
  const resumeSubscription = async () => {
    if (!orgId) return;
    setResuming(true);
    try {
      await apiCall(ENDPOINTS.ORG_RESUME_SUBSCRIPTION(orgId), { method: 'POST' });
      await reloadSub(orgId);
    } catch (e) { setSubMsg(e.message || 'Could not resume the subscription.'); }
    setResuming(false);
  };
  // client_secret for the embedded Stripe Checkout dialog (null = closed).
  const [checkoutSecret, setCheckoutSecret] = useState(null);
  // Available plans + the one picked in the reactivate/subscribe CTA (lets the user switch plan).
  const [plans, setPlans] = useState([]);
  const [chosenPlan, setChosenPlan] = useState('');
  useEffect(() => {
    apiCall(ENDPOINTS.PACKAGES).then((d) => setPlans((d && d.packages) || [])).catch(() => {});
  }, []);

  // Orgs this admin can bill for, with names, for the switcher tabs.
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    const isAdmin = localStorage.getItem('isOrgAdmin') === 'true'
      || localStorage.getItem('isStaff') === 'true'
      || localStorage.getItem('isSqipAdmin') === 'true';
    if (!isAdmin) navigate('/', { replace: true });
  }, [navigate]);

  // `orgAccess` in localStorage is only written at login, so an org provisioned since then
  // would be missing. Re-read it from /me/ and resolve each org's name for the tabs.
  useEffect(() => {
    let alive = true;
    (async () => {
      const me = await apiCall(ENDPOINTS.ME).catch(() => null);
      const ids = (me && Array.isArray(me.org_access) ? me.org_access : orgAccess).map(Number);
      if (!alive || ids.length === 0) return;
      if (me && Array.isArray(me.org_access)) {
        localStorage.setItem('orgAccess', JSON.stringify(me.org_access));
      }
      const list = await Promise.all(ids.map(async (id) => {
        const d = await apiCall(ENDPOINTS.ORG_DETAIL(id)).catch(() => null);
        return { id, name: (d && d.name) || `Organization #${id}` };
      }));
      if (!alive) return;
      setOrgs(list);
      // If the current selection isn't one of them (e.g. stale default), fall back to the first.
      if (!ids.includes(Number(orgId))) setOrgId(ids[0]);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared query params for the insights endpoint (window + bucket + category).
  const insightsParts = (days, grp, start, end, catId) => {
    const parts = [];
    if (start || end) {
      if (start) parts.push(`start=${start}`);
      if (end) parts.push(`end=${end}`);
    } else if (days) {
      parts.push(`days=${days}`);
    }
    if (grp) parts.push(`bucket=${grp}`);
    if (catId) parts.push(`category=${catId}`);
    return parts;
  };

  // The CSV must contain the WHOLE range, not the chart-capped window, so it re-fetches with
  // full=1 (which removes the bucket cap) before building the file.
  const downloadCsv = async () => {
    const parts = insightsParts(windowDays, bucket, startDate, endDate, insightCat);
    parts.push('full=1');
    const full = await apiCall(ENDPOINTS.ORG_INSIGHTS(orgId, parts.join('&'))).catch(() => insights);
    const catName = insightCat ? (insightCats.find((c) => String(c.id) === String(insightCat))?.name || '') : '';
    downloadUsageCsv(full || insights, catName ? `${orgName}_${catName}` : orgName);
  };

  const load = useCallback(async (id, days, grp, start, end, catId) => {
    if (!id) { setLoading(false); return; }
    setLoading(true); setError('');
    const parts = insightsParts(days, grp, start, end, catId);
    try {
      const [detail, subscription, ins] = await Promise.all([
        apiCall(ENDPOINTS.ORG_DETAIL(id)).catch(() => null),
        apiCall(ENDPOINTS.ORG_SUBSCRIPTION(id)).catch(() => null),
        apiCall(ENDPOINTS.ORG_INSIGHTS(id, parts.join('&'))).catch(() => null),
      ]);
      setOrgName(detail?.name || `Organization #${id}`);
      setSub(subscription && subscription.id ? subscription : null);
      setCaps((subscription && subscription.capabilities) || { analytics: false, advanced_analytics: false });
      setInsights(ins);
    } catch (e) { setError('Failed to load billing data.'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadSub = useCallback(async (id) => {
    const s = await apiCall(ENDPOINTS.ORG_SUBSCRIPTION(id)).catch(() => null);
    setSub(s && s.id ? s : null);
    if (s && s.capabilities) setCaps(s.capabilities);
  }, []);

  const loadPayments = useCallback(async (id, page) => {
    if (!id) return;
    setPayLoading(true);
    const data = await apiCall(ENDPOINTS.ORG_PAYMENTS(id, page)).catch(() => ({ results: [] }));
    setPayments(data.results || []);
    setPayCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
    setPayLoading(false);
  }, []);

  // Categories for the insights dropdown; reset the filter when the org changes.
  useEffect(() => {
    if (!orgId) return undefined;
    let alive = true;
    (async () => {
      const d = await apiCall(ENDPOINTS.CATEGORIES_BY_ORG(orgId)).catch(() => ({ results: [] }));
      if (!alive) return;
      setInsightCat('');
      setInsightCats(((d && d.results) || d || []).map((c) => ({ id: c.id, name: c.name || `Category #${c.id}` })));
    })();
    return () => { alive = false; };
  }, [orgId]);

  useEffect(() => {
    const t = setTimeout(() => load(orgId, windowDays, bucket, startDate, endDate, insightCat), 0);
    return () => clearTimeout(t);
  }, [orgId, windowDays, bucket, startDate, endDate, insightCat, load]);

  useEffect(() => {
    const t = setTimeout(() => loadPayments(orgId, payPage), 0);
    return () => clearTimeout(t);
  }, [orgId, payPage, loadPayments]);

  // Per-tab refresh, so checking on a webhook/payment doesn't mean reloading the whole page.
  // Tabs whose data lives here are re-fetched directly; self-fetching children (cards,
  // invoices, usage history, notification settings) re-run their effects off `refreshKey`.
  const refreshCurrentTab = useCallback(async () => {
    if (!orgId) return;
    setRefreshing(true);
    try {
      // The subscription header (status / plan / renewal) sits above every tab.
      await reloadSub(orgId);
      if (tab === 1) await load(orgId, windowDays, bucket, startDate, endDate, insightCat);
      if (tab === 2) await loadPayments(orgId, payPage);
      setRefreshKey((k) => k + 1);
    } finally {
      setRefreshing(false);
    }
  }, [orgId, tab, reloadSub, load, loadPayments, windowDays, bucket, startDate, endDate,
      insightCat, payPage]);

  const scfg = sub ? (SUB_STATUS[sub.status] || SUB_STATUS.incomplete) : null;

  // The plan the subscribe/reactivate CTA will use: the user's picker choice, else the current
  // or queued plan. Lets someone switch plans when reactivating a canceled subscription.
  const currentPlanSlug = sub ? ((sub.package && sub.package.slug)
    || (sub.pending_package && sub.pending_package.slug) || '') : '';
  const effectivePlan = chosenPlan || currentPlanSlug;

  // Upcoming charge — only when a card is on file (Stripe subscription linked). During a trial
  // that's the first charge at trial end; on an active plan it's the next renewal.
  // A scheduled change bills the NEW plan at the next cycle, so the upcoming charge shows the
  // pending plan's price when one is queued; otherwise the current plan.
  const upcomingPkg = sub && (sub.pending_package || sub.package);
  const upcomingDate = sub && (sub.status === 'trialing' ? sub.trial_ends_at : sub.current_period_end);
  // No upcoming charge when the subscription is ending at period end — it won't renew.
  const upcoming = (sub && sub.subscribed && !sub.cancel_at_period_end && upcomingPkg && upcomingDate) ? {
    amount: money(upcomingPkg.monthly_price, upcomingPkg.currency),
    date: fmt(upcomingDate),
    note: sub.status === 'trialing' ? 'first charge after your free trial' : 'next renewal',
  } : null;

  const startSubscribe = async () => {
    if (!orgId) return;
    setSubscribing(true); setSubMsg('');
    try {
      // Create an embedded Stripe Checkout session for this org's plan, then mount it in a dialog
      // (same embedded flow as onboarding). Stripe returns the customer to /admin/billing.
      const res = await apiCall(ENDPOINTS.ORG_SUBSCRIBE(orgId), {
        method: 'POST',
        body: JSON.stringify({ package: effectivePlan || undefined }),
      });
      if (res && res.client_secret) { setCheckoutSecret(res.client_secret); }
      else setSubMsg('Could not start checkout. Please try again.');
    } catch (e) {
      setSubMsg(e.status === 503
        ? 'Online payments are not enabled yet. Please contact support to activate your plan.'
        : e.status === 400
          ? 'Choose a plan before subscribing.'
          : (e.message || 'Could not start checkout.'));
    }
    setSubscribing(false);
  };

  // When Stripe returns the customer to /admin/billing?session_id=..., confirm the session and
  // refresh the subscription (the checkout.session.completed webhook is what actually flips state;
  // this is just so the page reflects it). Then strip the param so a refresh doesn't re-run it.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    let alive = true;
    (async () => {
      try {
        await apiCall(`${ENDPOINTS.CHECKOUT_SESSION_STATUS}?session_id=${encodeURIComponent(sessionId)}`);
      } catch { /* non-fatal — webhook is authoritative */ }
      if (!alive) return;
      setCheckoutSecret(null);
      setSubMsg('Thanks! Your subscription is being set up. It may take a moment to appear.');
      if (orgId) load(orgId, windowDays, bucket, startDate, endDate, insightCat);
      navigate('/admin/billing', { replace: true });
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 3, sm: 6 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Fade in timeout={400}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>Billing &amp; Insights</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{orgName || 'Your organization'}</Typography>
            </Box>
          </Box>
        </Fade>

        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        {!orgId && !loading && <Alert severity="info" sx={{ borderRadius: 2 }}>No organization is linked to your account yet.</Alert>}

        {/* One tab per organization this admin bills for. */}
        {orgs.length > 1 && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Tabs
              value={Math.max(0, orgs.findIndex((o) => o.id === Number(orgId)))}
              onChange={(_, i) => { setOrgId(orgs[i].id); setPayPage(1); }}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            >
              {orgs.map((o) => <Tab key={o.id} label={o.name} sx={{ fontWeight: 700, minHeight: 48 }} />)}
            </Tabs>
          </Paper>
        )}

        {orgId && (
          <>
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ flex: 1, minWidth: 0 }}>
                <Tab value={0} icon={<ReceiptLongOutlinedIcon fontSize="small" />} iconPosition="start" label="Subscription" sx={{ minHeight: 56 }} />
                {caps.analytics && (
                  <Tab value={1} icon={<EventAvailableOutlinedIcon fontSize="small" />} iconPosition="start" label="Insights" sx={{ minHeight: 56 }} />
                )}
                <Tab value={2} icon={<PaymentsOutlinedIcon fontSize="small" />} iconPosition="start" label="Payments" sx={{ minHeight: 56 }} />
                {sub && sub.usage && (
                  <Tab value={4} icon={<DataUsageOutlinedIcon fontSize="small" />} iconPosition="start" label="Usage" sx={{ minHeight: 56 }} />
                )}
                <Tab value={3} icon={<NotificationsNoneOutlinedIcon fontSize="small" />} iconPosition="start" label="Notifications" sx={{ minHeight: 56 }} />
              </Tabs>
              <Tooltip title="Refresh this tab">
                {/* span so the Tooltip still works while the button is disabled */}
                <span>
                  <IconButton
                    onClick={refreshCurrentTab}
                    disabled={refreshing}
                    aria-label="Refresh this tab"
                    sx={{ mr: 1, flexShrink: 0 }}
                  >
                    {refreshing
                      ? <CircularProgress size={18} />
                      : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>

            {/* SUBSCRIPTION */}
            {tab === 0 && (
              <Paper elevation={0} sx={cardSx}>
                {loading ? <Skeleton height={80} /> : sub === null ? (
                  <Typography variant="body2" color="text.secondary">No subscription yet.</Typography>
                ) : sub && (
                  <>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" label={scfg.label} sx={{ bgcolor: scfg.bg, color: scfg.color, fontWeight: 600, mt: 0.5 }} /></Box></Grid>
                    <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Package</Typography><Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{sub.package ? sub.package.name : (sub.pending_package ? `${sub.pending_package.name} (after trial)` : (sub.plan_label || '—'))}</Typography></Grid>
                    <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Trial ends</Typography><Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{sub.status === 'trialing' ? `${fmt(sub.trial_ends_at)}${sub.days_left != null ? ` (${sub.days_left}d)` : ''}` : '—'}</Typography></Grid>
                    <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">{sub.status === 'canceled' ? 'Ended' : (sub.cancel_at_period_end ? 'Ends' : 'Renews')}</Typography><Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{sub.status === 'canceled' ? fmt(sub.canceled_at || sub.current_period_end) : fmt(sub.current_period_end)}</Typography></Grid>
                  </Grid>
                  {/* Embedded billing management — cards, invoices, retry failed payment, billing
                      details, coupons — for any org with a card on file (active or trialing). */}
                  {sub && sub.subscribed && (
                    <BillingManageCard orgId={orgId} sub={sub} onChanged={() => reloadSub(orgId)} refreshKey={refreshKey} />
                  )}
                  {/* Hide the "Your plan / scheduled change" block once canceled — the plan no
                      longer applies; the Pay & subscribe CTA below is what's relevant then. */}
                  {sub.status !== 'canceled' && (
                    <PackageGrants
                      pkg={sub.package}
                      isTrial={sub.status === 'trialing' || sub.status === 'incomplete'}
                      orgId={orgId}
                      pending={sub.pending_package}
                      pendingAt={sub.pending_package_effective_at}
                      startedAt={sub.current_period_start || sub.trial_started_at}
                      ending={sub.cancel_at_period_end}
                      onChosen={() => reloadSub(orgId)}
                    />
                  )}
                  </>
                )}
                {!loading && (sub === null || sub.status !== 'active') && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {sub && sub.subscribed ? (
                      // Card captured (Stripe subscription linked): show "Subscribed" and drop the CTA,
                      // even while the trial runs — Stripe charges automatically when it ends.
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Subscribed</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Your card is on file.{sub.status === 'trialing' && sub.trial_ends_at
                              ? ` Billing starts ${fmt(sub.trial_ends_at)}.`
                              : ' Your subscription is being activated.'}
                          </Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {sub && sub.status === 'trialing' ? 'On a free trial'
                              : sub && sub.status === 'canceled' ? 'Subscription canceled'
                                : 'Not subscribed'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sub && sub.status === 'canceled'
                              ? 'Subscribe again to reactivate your account and keep your queue live.'
                              : 'Subscribe to keep your queue active after the trial.'}
                          </Typography>
                        </Box>
                        {/* When reactivating/activating (not mid-trial), let the user switch plan. */}
                        {sub && sub.status !== 'trialing' && plans.length > 0 && (
                          <FormControl size="small" sx={{ minWidth: 190 }}>
                            <InputLabel id="reactivate-plan">Plan</InputLabel>
                            <Select labelId="reactivate-plan" label="Plan" value={effectivePlan}
                              onChange={(e) => setChosenPlan(e.target.value)}
                              sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                              {plans.map((p) => (
                                <MenuItem key={p.slug} value={p.slug}>{p.name} — ${p.monthly_price}/mo</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <Button variant="contained" onClick={startSubscribe} disabled={subscribing || (sub && sub.status !== 'trialing' && !effectivePlan)}>
                          {subscribing ? <CircularProgress size={18} />
                            : (sub && sub.status === 'trialing' ? 'Subscribe now' : 'Pay & subscribe')}
                        </Button>
                      </Stack>
                    )}
                    {subMsg && <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>{subMsg}</Alert>}
                  </Box>
                )}

                {/* Cancellation (org admin) — trial deactivates now, paid ends at period end.
                    Hidden once already ending / canceled so it can't be cancelled twice. */}
                {!loading && sub && !sub.cancel_at_period_end && sub.status !== 'canceled' && (
                  <CancelSubscriptionCard
                    orgId={orgId}
                    sub={sub}
                    onCancelled={() => reloadSub(orgId)}
                  />
                )}
                {!loading && sub && sub.cancel_at_period_end && (
                  <Alert
                    severity="info" sx={{ mt: 2, borderRadius: 2 }}
                    action={(
                      <Button color="inherit" size="small" disabled={resuming}
                        onClick={resumeSubscription} sx={{ fontWeight: 600 }}>
                        {resuming ? <CircularProgress size={16} /> : 'Resume'}
                      </Button>
                    )}
                  >
                    Your subscription is set to end on <strong>{fmt(sub.current_period_end)}</strong>. You keep full access until then — resume any time to keep it.
                  </Alert>
                )}
              </Paper>
            )}

            {/* INSIGHTS */}
            {tab === 1 && (
              <Paper elevation={0} sx={cardSx}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="win-sel">Window</InputLabel>
                    <Select labelId="win-sel" label="Window" value={windowDays} onChange={(e) => setWindowDays(e.target.value)} disabled={!!(startDate || endDate)}>
                      {WINDOWS.map((w) => <MenuItem key={w.days} value={w.days}>{w.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="grp-sel">Group by</InputLabel>
                    <Select labelId="grp-sel" label="Group by" value={bucket} onChange={(e) => setBucket(e.target.value)}>
                      {['day', 'week', 'month', 'year'].map((b) => <MenuItem key={b} value={b}>{b[0].toUpperCase() + b.slice(1)}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="cat-sel">Category</InputLabel>
                    <Select labelId="cat-sel" label="Category" value={insightCat} onChange={(e) => setInsightCat(e.target.value)}>
                      <MenuItem value="">All categories</MenuItem>
                      {insightCats.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 140 }} />
                  <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 140 }} />
                  {(startDate || endDate) && <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); }}>Clear dates</Button>}
                  <Box sx={{ flex: 1 }} />
                  {/* CSV export is an Advanced Analytics capability (Enterprise). */}
                  {caps.advanced_analytics && (
                    <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled={!insights} onClick={downloadCsv}>CSV</Button>
                  )}
                </Stack>
                {loading ? <Skeleton height={90} /> : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}><StatCard icon={<EventAvailableOutlinedIcon color="action" />} label="Appointments made" value={insights?.appointments_made} /></Grid>
                    <Grid item xs={12} sm={4}><StatCard icon={<SmsOutlinedIcon color="action" />} label="SMS sent" value={insights?.sms_sent} /></Grid>
                    <Grid item xs={12} sm={4}><StatCard icon={<EmailOutlinedIcon color="action" />} label="Emails sent" value={insights?.email_sent} /></Grid>
                  </Grid>
                )}
                {!loading && <InsightsBreakdown data={insights} />}
                {!loading && (
                  <Box sx={{ mt: 2 }}>
                    {insights?.series_truncated && (
                      <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0 }}>
                        Totals above cover the full range; the charts and CSV show only the most recent{' '}
                        {(insights?.appointments_series || []).length} {insights?.bucket || bucket}s.
                        Pick a date range to include everything.
                      </Alert>
                    )}
                    <StackedBars dates={insights?.dates} bucket={insights?.bucket || bucket} label={`Appointments by ${insights?.bucket || bucket}`}
                      groups={[
                        { key: 'walk_in', label: 'Walk-in', color: '#1565C0', series: insights?.appointments_by_kind?.walk_in || [] },
                        { key: 'scheduled', label: 'Scheduled', color: '#00C4CC', series: insights?.appointments_by_kind?.scheduled || [] },
                      ]} />
                    <StackedBars dates={insights?.dates} bucket={insights?.bucket || bucket} label={`SMS by ${insights?.bucket || bucket}`} groups={typeGroups(insights?.sms_by_type)} />
                    <StackedBars dates={insights?.dates} bucket={insights?.bucket || bucket} label={`Emails by ${insights?.bucket || bucket}`} groups={typeGroups(insights?.email_by_type)} />
                  </Box>
                )}
                {!loading && insights?.by_category?.length > 0 && (
                  <Box sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Appointments</TableCell>
                        <TableCell align="right">SMS</TableCell>
                        <TableCell align="right">Email</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {insights.by_category.map((c) => (
                          <TableRow key={c.category_id}>
                            <TableCell>{c.name || `#${c.category_id}`}</TableCell>
                            <TableCell align="right">{c.appointments}</TableCell>
                            <TableCell align="right">{c.sms ?? c.messages_sent}</TableCell>
                            <TableCell align="right">{c.email ?? 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            )}

            {/* PAYMENTS */}
            {tab === 2 && (
              <Paper elevation={0} sx={cardSx}>
                {upcoming && (
                  <Alert icon={<EventAvailableOutlinedIcon fontSize="small" />} severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <strong>Upcoming charge:</strong> {upcoming.amount} on {upcoming.date} — {upcoming.note}
                  </Alert>
                )}
                {payLoading ? <Skeleton height={120} /> : payments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>{upcoming ? 'No charges yet — your first one is shown above.' : 'No payments yet.'}</Typography>
                ) : (
                  <>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Description</TableCell><TableCell>Status</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                        <TableBody>
                          {payments.map((p) => (
                            <TableRow key={p.id}>
                              <TableCell>{fmt(p.paid_at || p.created_at)}</TableCell>
                              <TableCell>{p.description || '—'}</TableCell>
                              <TableCell><Chip size="small" label={p.status} variant="outlined" /></TableCell>
                              <TableCell align="right">{money(p.amount, p.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    {payCount > PAY_PAGE_SIZE && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination count={Math.ceil(payCount / PAY_PAGE_SIZE)} page={payPage} onChange={(_, p) => setPayPage(p)} color="primary" size="small" />
                      </Box>
                    )}
                  </>
                )}
                {/* Stripe invoices with downloadable PDFs (source-of-truth receipts). */}
                {sub && sub.subscribed && <StripeInvoices orgId={orgId} refreshKey={refreshKey} />}
              </Paper>
            )}

            {/* NOTIFICATIONS */}
            {/* USAGE */}
            {tab === 4 && (
              <Paper elevation={0} sx={cardSx}>
                {sub && sub.usage
                  ? (<>
                      <UsagePanel usage={sub.usage} standalone />
                      <UsageHistory orgId={orgId} refreshKey={refreshKey} />
                    </>)
                  : <Typography variant="body2" color="text.secondary">No usage data yet.</Typography>}
              </Paper>
            )}

            {tab === 3 && <NotificationsCard orgId={orgId} refreshKey={refreshKey} />}
          </>
        )}
      </Box>

      {/* Embedded Stripe Checkout for self-serve subscribe (trial -> paid, or reactivate). */}
      <Dialog
        open={!!checkoutSecret}
        onClose={() => setCheckoutSecret(null)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Subscribe</DialogTitle>
        <DialogContent dividers>
          {checkoutSecret && (
            <StripePaymentForm
              clientSecret={checkoutSecret}
              onError={(m) => { setCheckoutSecret(null); setSubMsg(m || 'Could not start checkout.'); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
